/**
 * Outlaw Sign-in's read API for the signed-in person's methods (OWA-1191; the endpoint is
 * OWA-1188, `GET /signin/api/v1/me/sign-in`) and the URLs of its method pages
 * (`/signin/methods/{group}/{operation}`).
 *
 * @module client/Auth/signInMethods
 */

import IDP_CONFIG from './idp-config';
import {tabSession} from './session';

export interface PasskeyMethod {id: string; name: string; created_at: string}
export interface SecondFactorMethod {id: string; kind: 'totp' | 'email'; label: string; detail: string}
export interface SignedInDevice {id: string; label: string; location: string; last_seen_at: string; current: boolean}

export interface SignInMethods {
    passkeys: PasskeyMethod[];
    second_factors: SecondFactorMethod[];
    devices: SignedInDevice[];
    /** True only for an account that may use a password and second factors (a Client Portal
     * client with no staff role anywhere). Missing or anything else means passkeys only. */
    password_allowed: boolean;
}

export type MethodPageAction =
    | {page: 'passkey/add'}
    | {page: 'passkey/remove'; id: string}
    | {page: 'factor/add'; kind: SecondFactorMethod['kind']}
    | {page: 'factor/remove'; id: string}
    | {page: 'password'}
    | {page: 'device/sign-out'; id: string};

/** The Outlaw Sign-in host. It is the identity provider's host until the sign-in app has its own. */
export function signInHost(): string {
    return IDP_CONFIG.domain;
}

/** The signed-in person's subject, which binds a method page to this app's person. */
function tabSubject(): string | undefined {
    return tabSession.getIdentity()?.subject;
}

/** The page on Outlaw Sign-in that performs `action` for `subject`, returning to `returnUrl`
 * afterward. Outlaw Sign-in refuses to act for anyone but `subject`. */
export function methodPageUrl(
    action: MethodPageAction, returnUrl: string, host = signInHost(), subject = tabSubject(),
): string {
    const url = new URL(`https://${host}/signin/methods/${action.page}`);
    if (subject) url.searchParams.set('sub', subject);
    if ('id' in action) url.searchParams.set('id', action.id);
    if ('kind' in action) url.searchParams.set('kind', action.kind);
    url.searchParams.set('return', returnUrl);
    return url.href;
}

export class SignInMethodsError extends Error {}

const isObject = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const isText = (v: unknown): v is string => typeof v === 'string';

function list<T>(value: unknown, valid: (row: Record<string, unknown>) => boolean): T[] {
    if (!Array.isArray(value) || !value.every(row => isObject(row) && valid(row))) {
        throw new SignInMethodsError('Outlaw Sign-in returned a list this page cannot read.');
    }
    return value as T[];
}

/** Check the response against the contract; anything else fails rather than rendering a guess. */
export function parseSignInMethods(body: unknown): SignInMethods {
    if (!isObject(body)) throw new SignInMethodsError('Outlaw Sign-in returned an answer this page cannot read.');
    return {
        passkeys: list(body.passkeys, r => isText(r.id) && isText(r.name) && isText(r.created_at)),
        second_factors: list(body.second_factors, r => isText(r.id) && (r.kind === 'totp' || r.kind === 'email') &&
            isText(r.label) && isText(r.detail)),
        devices: list(body.devices, r => isText(r.id) && isText(r.label) && isText(r.location) &&
            isText(r.last_seen_at) && typeof r.current === 'boolean'),
        password_allowed: body.password_allowed === true,
    };
}

export type AccessToken = () => Promise<string>;

const tabAccessToken: AccessToken = async () => (await tabSession.credentials()).accessToken;

/** Read the signed-in person's passkeys, second factors and devices from Outlaw Sign-in. */
export async function fetchSignInMethods(
    options: {host?: string; accessToken?: AccessToken; fetcher?: typeof fetch; signal?: AbortSignal} = {},
): Promise<SignInMethods> {
    const {host = signInHost(), accessToken = tabAccessToken, fetcher = fetch, signal} = options;
    // The device cookie lets Outlaw Sign-in mark which listed device is this one. It is SameSite=Lax,
    // so it arrives only when the app shares Sign-in's site (an outlaw.app host); elsewhere no device
    // is marked and My Profile says so.
    const response = await fetcher(`https://${host}/signin/api/v1/me/sign-in`, {
        headers: {Authorization: `Bearer ${await accessToken()}`, Accept: 'application/json'},
        credentials: 'include',
        signal,
    });
    if (!response.ok) throw new SignInMethodsError(`Outlaw Sign-in answered ${response.status}.`);
    return parseSignInMethods(await response.json());
}
